import { useCallback, useEffect, useRef, useState } from 'react';
import '../styles/manageUsers.css';
import { useCurrentUser } from '../contexts/UserContext';
import { lookupUserByEmail, createUser } from '../services/userService';
import { getMentors, scheduleDemoInvite, getDemoInvites, getDemoQueue } from '../services/demoService';
import { listCourses } from '../services/courseService';
import { createEnrollment, getStudentEnrollments, getEnrollmentStats } from '../services/enrollmentService';

// ─── helpers ────────────────────────────────────────────────────────────────

function initials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

function getDefaultSlot() {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setMinutes(0, 0, 0);
  return d.toISOString().slice(0, 16);
}

const STATUS_MAP = {
  active:         { label: 'Active',         cls: 'badge-active' },
  inactive:       { label: 'Inactive',       cls: 'badge-inactive' },
  suspended:      { label: 'Suspended',      cls: 'badge-warn' },
  scheduled:      { label: 'Scheduled',      cls: 'badge-info' },
  completed:      { label: 'Completed',      cls: 'badge-active' },
  dropped:        { label: 'Dropped',        cls: 'badge-warn' },
  archived:       { label: 'Archived',       cls: 'badge-inactive' },
  demo_scheduled: { label: 'Demo Scheduled', cls: 'badge-info' },
  demo_completed: { label: 'Demo Done',      cls: 'badge-active' },
  no_show:        { label: 'No Show',        cls: 'badge-warn' },
};

function StatusBadge({ status }) {
  const { label, cls } = STATUS_MAP[status] || { label: status || 'Unknown', cls: 'badge-inactive' };
  return <span className={`mu-badge ${cls}`}>{label}</span>;
}

// ─── LoadingDots ─────────────────────────────────────────────────────────────

function LoadingDots() {
  return (
    <span className="mu-loading-dots" aria-label="Loading">
      <span /><span /><span />
    </span>
  );
}

// ─── SearchBar ───────────────────────────────────────────────────────────────

function SearchBar({ value, onChange, onSearch, loading, children }) {
  const inputRef = useRef(null);
  const handleKey = (e) => { if (e.key === 'Enter') onSearch(); };

  return (
    <div className="mu-search-row">
      <div className="mu-search-field">
        <svg className="mu-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          ref={inputRef}
          className="mu-input"
          type="email"
          placeholder="Enter student email address…"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKey}
          autoComplete="off"
          spellCheck={false}
        />
        {value && (
          <button className="mu-search-clear" onClick={() => { onChange(''); inputRef.current?.focus(); }} aria-label="Clear">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>
      <button className="secondary-btn mu-lookup-btn" onClick={onSearch} disabled={loading || !value.trim()}>
        {loading ? <LoadingDots /> : 'Look Up'}
      </button>
      {children}
    </div>
  );
}

// ─── StudentProfile ───────────────────────────────────────────────────────────

function StudentProfile({ user }) {
  const name = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
  return (
    <div className="mu-profile">
      <div className="mu-avatar">{initials(name)}</div>
      <div className="mu-profile-body">
        <p className="mu-profile-name">{name}</p>
        <p className="mu-profile-email">{user.email || user.emailAddress}</p>
        <div className="mu-profile-chips">
          <span className="mu-chip">{user.role}</span>
          <StatusBadge status={user.status} />
          {user.tagline && <span className="mu-chip">{user.tagline}</span>}
        </div>
      </div>
    </div>
  );
}

// ─── EnrollmentsCard ──────────────────────────────────────────────────────────

const ENROLLMENT_STATUSES = ['active', 'completed', 'dropped', 'archived'];

function EnrollmentsCard({ student, courses, coursesLoading }) {
  const studentId = student.id || student._id;

  const [enrollments, setEnrollments]     = useState([]);
  const [envLoading, setEnvLoading]       = useState(true);
  const [envError, setEnvError]           = useState('');
  const [showForm, setShowForm]           = useState(false);
  const [form, setForm]                   = useState({ courseId: '', status: 'active', notes: '' });
  const [saving, setSaving]               = useState(false);
  const [saveMsg, setSaveMsg]             = useState(null); // { ok, text }

  useEffect(() => {
    let cancelled = false;
    setEnvLoading(true);
    setEnvError('');
    getStudentEnrollments(studentId)
      .then((data) => { if (!cancelled) setEnrollments(data?.enrollments || []); })
      .catch((err) => { if (!cancelled) setEnvError(err.message || 'Unable to load enrollments.'); })
      .finally(() => { if (!cancelled) setEnvLoading(false); });
    return () => { cancelled = true; };
  }, [studentId]);

  const enrolledIds = new Set(enrollments.map((e) => String(e.course?.id)));
  const available = courses.filter((c) => !enrolledIds.has(String(c._id)));

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.courseId) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const data = await createEnrollment({ studentId, courseId: form.courseId, status: form.status, notes: form.notes });
      if (data?.enrollment) {
        setEnrollments((prev) => [data.enrollment, ...prev]);
        setForm({ courseId: '', status: 'active', notes: '' });
        setShowForm(false);
        setSaveMsg({ ok: true, text: 'Enrollment added.' });
      }
    } catch (err) {
      setSaveMsg({ ok: false, text: err.message || 'Failed to add enrollment.' });
    } finally {
      setSaving(false);
    }
  };

  return (
      <div className="mu-card">
        <div className="mu-card-head">
          <div>
            <p className="eyebrow">Enrollments</p>
          </div>
        <button
          className="mu-ghost-btn"
          onClick={() => { setShowForm((v) => !v); setSaveMsg(null); }}
        >
          {showForm ? 'Cancel' : '+ Add'}
        </button>
      </div>

      {showForm && (
        <form className="mu-inline-form" onSubmit={handleAdd}>
          <div className="mu-field-row">
            <div className="mu-field">
              <label className="mu-label">Course</label>
              <select
                className="mu-select"
                value={form.courseId}
                onChange={(e) => setForm((f) => ({ ...f, courseId: e.target.value }))}
                required
                disabled={coursesLoading}
              >
                <option value="">{coursesLoading ? 'Loading…' : 'Select a course'}</option>
                {available.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.title} · Grade {c.grade} · {c.subject}
                  </option>
                ))}
              </select>
            </div>
            <div className="mu-field mu-field-sm">
              <label className="mu-label">Status</label>
              <select
                className="mu-select"
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              >
                {ENROLLMENT_STATUSES.map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mu-field">
            <label className="mu-label">Notes (optional)</label>
            <textarea
              className="mu-textarea"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Any context about this enrollment…"
            />
          </div>
          <div className="mu-form-foot">
            <button className="mu-dark-btn" type="submit" disabled={!form.courseId || saving}>
              {saving ? <LoadingDots /> : 'Add enrollment'}
            </button>
            {saveMsg && (
              <p className={saveMsg.ok ? 'success-text' : 'error-text'}>{saveMsg.text}</p>
            )}
          </div>
        </form>
      )}

      {!showForm && saveMsg?.ok && (
        <p className="success-text mu-save-confirm">{saveMsg.text}</p>
      )}

      <div className="mu-enrollment-grid">
        {envLoading && <p className="mu-muted">Loading enrollments…</p>}
        {!envLoading && envError && <p className="error-text">{envError}</p>}
        {!envLoading && !envError && enrollments.length === 0 && (
          <p className="mu-muted">No enrollments yet.</p>
        )}
        {enrollments.map((e) => (
          <div key={e.id} className="mu-enrollment-card">
            <div className="mu-enrollment-card-head">
              <div>
                <p className="mu-list-title">{e.course?.title || 'Course'}</p>
                <p className="mu-muted">Grade {e.course?.grade} · {e.course?.subject}</p>
              </div>
              <StatusBadge status={e.status} />
            </div>
            <p className="mu-enrollment-date">{formatDate(e.assignedAt)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── MentorTypeahead ──────────────────────────────────────────────────────────

function MentorTypeahead({ mentors, value, onChange, required }) {
  const [query, setQuery]       = useState('');
  const [open, setOpen]         = useState(false);
  const [focused, setFocused]   = useState(false);
  const wrapRef                 = useRef(null);

  const selected = mentors.find((m) => m.id === value) || null;

  // Close on outside click
  useEffect(() => {
    function handler(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.trim()
    ? mentors.filter((m) =>
        m.name.toLowerCase().includes(query.toLowerCase()) ||
        (m.email && m.email.toLowerCase().includes(query.toLowerCase()))
      )
    : mentors;

  const handleSelect = (mentor) => {
    onChange(mentor.id);
    setQuery('');
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setQuery('');
    setOpen(true);
  };

  const displayValue = selected
    ? selected.name + (selected.title && selected.title !== 'Mentor' ? ` · ${selected.title}` : '')
    : query;

  return (
    <div className="mu-typeahead" ref={wrapRef}>
      <div className={`mu-typeahead-input-wrap ${focused ? 'focused' : ''} ${selected ? 'has-value' : ''}`}>
        <input
          className="mu-typeahead-input"
          type="text"
          placeholder="Search mentors…"
          value={selected ? displayValue : query}
          readOnly={Boolean(selected)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { setFocused(true); if (!selected) setOpen(true); }}
          onBlur={() => setFocused(false)}
          required={required && !value}
          autoComplete="off"
        />
        {selected ? (
          <button type="button" className="mu-typeahead-clear" onClick={handleClear} aria-label="Clear">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        ) : (
          <svg className="mu-typeahead-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        )}
      </div>

      {open && !selected && (
        <ul className="mu-typeahead-dropdown">
          {filtered.length === 0 ? (
            <li className="mu-typeahead-empty">No mentors match "{query}"</li>
          ) : (
            filtered.map((m) => (
              <li key={m.id} className="mu-typeahead-option" onMouseDown={() => handleSelect(m)}>
                <span className="mu-typeahead-name">{m.name}</span>
                {m.email && <span className="mu-typeahead-meta">{m.email}</span>}
              </li>
            ))
          )}
        </ul>
      )}

    </div>
  );
}

// ─── DemoInviteCard ────────────────────────────────────────────────────────────

function DemoInviteCard({ searchEmail, mentors, mentorsLoading, salesUser, existingInvites = [], inviteLoading, onRefreshInvites }) {
  const buildInitialForm = () => ({
    prospectName: '',
    mentorId: '',
    timeSlot: getDefaultSlot(),
    notes: '',
  });
  const [form, setForm] = useState(buildInitialForm);
  const [state, setState] = useState({ loading: false, error: '', newInvite: null, delivery: null });
  // null = auto (defaults to 'view' when invites exist, 'send' when empty)
  // 'view' | 'send' = explicit user choice
  const [userMode, setUserMode] = useState(null);
  const scrollRef = useRef(null);
  const [scrollIndex, setScrollIndex] = useState(0);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const selectedMentor = mentors.find((m) => m.id === form.mentorId);
  const invites = state.newInvite ? [state.newInvite, ...(existingInvites || [])] : (existingInvites || []);
  const hasInvite = invites.length > 0;
  const multipleInvites = invites.length > 1;

  // Derive effective mode: respect explicit user choice, otherwise auto based on invites
  const effectiveMode = userMode !== null ? userMode : (hasInvite ? 'view' : 'send');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setState({ loading: true, error: '', newInvite: null, delivery: null });
    try {
      const data = await scheduleDemoInvite({
        email: searchEmail,
        prospectName: form.prospectName,
        mentorId: form.mentorId,
        timeSlot: form.timeSlot,
        notes: form.notes,
        salesPersonEmail: salesUser?.email || salesUser?.emailAddress,
      });
      setState({ loading: false, error: '', newInvite: data?.invite || null, delivery: data?.emailDelivery || null });
      setUserMode('view');
      setScrollIndex(0);
      setForm(buildInitialForm());
      onRefreshInvites?.();
    } catch (err) {
      setState({ loading: false, error: err.message || 'Unable to send invitation.', newInvite: null, delivery: null });
    }
  };

  // Reset form and mode when the searched email changes
  useEffect(() => {
    setForm(buildInitialForm());
    setState({ loading: false, error: '', newInvite: null, delivery: null });
    setUserMode(null);
    setScrollIndex(0);
  }, [searchEmail]);

  // Once the DB refresh lands with real data, drop the optimistic newInvite
  // so it doesn't duplicate alongside the same record from existingInvites.
  useEffect(() => {
    if (existingInvites?.length > 0) {
      setState((prev) => prev.newInvite ? { ...prev, newInvite: null } : prev);
    }
  }, [existingInvites]);

  // Track scroll position to update index indicator
  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, offsetWidth } = scrollRef.current;
    setScrollIndex(Math.round(scrollLeft / offsetWidth));
  };

  const viewingDemos = hasInvite && effectiveMode === 'view';
  const viewingForm  = effectiveMode === 'send';

  return (
    <div className="mu-card">
      <div className="mu-card-head mu-card-head-tight">
        <p className="eyebrow">Demo invitation</p>
        {hasInvite && effectiveMode === 'send' && (
          <button type="button" className="mu-switch-btn" onClick={() => setUserMode('view')}>
            View scheduled demo
          </button>
        )}
        {effectiveMode === 'view' && (
          <button type="button" className="mu-switch-btn" onClick={() => setUserMode('send')}>
            Send new invitation
          </button>
        )}
      </div>

      {inviteLoading && !hasInvite && (
        <p className="mu-muted">Checking for existing invitations…</p>
      )}

      {viewingDemos && (
        <>
          <div
            className={`mu-demo-scroll${multipleInvites ? ' mu-demo-scroll--multi' : ''}`}
            ref={scrollRef}
            onScroll={multipleInvites ? handleScroll : undefined}
          >
            {invites.map((invite) => (
              <div key={invite.id || invite.createdAt} className="mu-demo-card mu-demo-card--snap">
                <div className="mu-demo-card-header">
                  <div>
                    <p className="mu-demo-label">Mentor</p>
                    <h3>{invite.mentor?.name || 'Mentor assigned'}</h3>
                  </div>
                  <StatusBadge status={invite.status} />
                </div>

                <div className="mu-demo-info-grid">
                  <div>
                    <p className="mu-demo-label">Scheduled by</p>
                    <p className="mu-demo-value">{invite.salesPersonName || salesUser?.name || '—'}</p>
                  </div>
                  <div>
                    <p className="mu-demo-label">Time</p>
                    <p className="mu-demo-value">{formatDate(invite.timeSlot)}</p>
                  </div>
                  {invite.meeting?.joinUrl && (
                    <div>
                      <p className="mu-demo-label">Join link</p>
                      <a className="mu-demo-link" href={invite.meeting.joinUrl} target="_blank" rel="noopener noreferrer">
                        Open meeting
                      </a>
                    </div>
                  )}
                </div>

                {invite.notes && (
                  <p className="mu-demo-notes">Notes: {invite.notes}</p>
                )}
              </div>
            ))}
          </div>

          {multipleInvites && (
            <div className="mu-demo-scroll-indicator">
              {invites.map((_, i) => (
                <span key={i} className={`mu-scroll-dot${i === scrollIndex ? ' mu-scroll-dot--active' : ''}`} />
              ))}
              <span className="mu-scroll-count">{scrollIndex + 1} of {invites.length}</span>
            </div>
          )}
        </>
      )}

      {viewingForm && (
        <form className="mu-form mu-inline-form-grid" onSubmit={handleSubmit}>
          <div className="mu-inline-row">
            <label className="mu-inline-label">Prospect Name</label>
            <input className="mu-input" type="text" placeholder="Full name (optional)"
              value={form.prospectName} onChange={(e) => set('prospectName', e.target.value)} />
          </div>

          <div className="mu-inline-row">
            <label className="mu-inline-label">Student Email</label>
            <input className="mu-input mu-input-readonly" type="email" value={searchEmail} readOnly />
          </div>

          <div className="mu-inline-row">
            <label className="mu-inline-label">Mentor <span className="mu-required">*</span></label>
            {mentorsLoading ? (
              <p className="mu-muted">Loading mentors…</p>
            ) : (
              <MentorTypeahead
                mentors={mentors}
                value={form.mentorId}
                onChange={(id) => set('mentorId', id)}
                required
              />
            )}
          </div>

          <div className="mu-inline-row">
            <label className="mu-inline-label">Preferred Time <span className="mu-required">*</span></label>
            <input className="mu-input" type="datetime-local" value={form.timeSlot}
              onChange={(e) => set('timeSlot', e.target.value)} required />
          </div>

          <div className="mu-field">
            <label className="mu-label">Notes for Mentor</label>
            <textarea className="mu-textarea" rows={3}
              placeholder="Anything the mentor should know before the call…"
              value={form.notes} onChange={(e) => set('notes', e.target.value)} />
          </div>

          {(salesUser?.email || selectedMentor?.email) && (
            <p className="mu-cc-note">
              CC:{' '}
              {[salesUser?.email || salesUser?.emailAddress, selectedMentor?.email]
                .filter(Boolean).join(', ')}
            </p>
          )}

          <div className="mu-form-foot">
            <button className="mu-dark-btn" type="submit"
              disabled={state.loading || !form.mentorId || !form.timeSlot}>
              {state.loading ? <LoadingDots /> : 'Send demo invitation'}
            </button>
            {state.error && <p className="error-text">{state.error}</p>}
          </div>
        </form>
      )}
    </div>
  );
}

// ─── AddUserCard ───────────────────────────────────────────────────────────────

const ENROLLMENT_STATUSES_ADD = ['active', 'completed', 'dropped', 'archived'];

function AddUserCard({ prefillEmail, courses, coursesLoading }) {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: prefillEmail || '', grade: '' });
  const [saveState, setSaveState] = useState({ loading: false, error: '', studentId: null });

  // Enrollment state — shown after student is saved
  const [enrollments, setEnrollments]     = useState([]); // successfully added so far
  const [enrForm, setEnrForm]             = useState({ courseId: '', status: 'active' });
  const [enrState, setEnrState]           = useState({ loading: false, error: '', msg: '' });

  const set    = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setEnr = (k, v) => setEnrForm((f) => ({ ...f, [k]: v }));

  const enrolledIds  = new Set(enrollments.map((e) => e.courseId));
  const availableCourses = courses.filter((c) => !enrolledIds.has(String(c._id)));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaveState({ loading: true, error: '', studentId: null });
    try {
      const data = await createUser({ firstName: form.firstName, lastName: form.lastName, email: form.email, grade: form.grade });
      setSaveState({ loading: false, error: '', studentId: data.user.id });
    } catch (err) {
      setSaveState({ loading: false, error: err.message || 'Failed to add student.', studentId: null });
    }
  };

  const handleEnroll = async (e) => {
    e.preventDefault();
    if (!enrForm.courseId || !saveState.studentId) return;
    setEnrState({ loading: true, error: '', msg: '' });
    try {
      await createEnrollment({ studentId: saveState.studentId, courseId: enrForm.courseId, status: enrForm.status });
      const added = courses.find((c) => String(c._id) === enrForm.courseId);
      setEnrollments((prev) => [...prev, { courseId: enrForm.courseId, title: added?.title, subject: added?.subject, grade: added?.grade }]);
      setEnrForm({ courseId: '', status: 'active' });
      setEnrState({ loading: false, error: '', msg: `${added?.title || 'Course'} enrolled.` });
    } catch (err) {
      setEnrState({ loading: false, error: err.message || 'Failed to enroll.', msg: '' });
    }
  };

  const studentSaved = Boolean(saveState.studentId);

  return (
    <div className="mu-card">
      <p className="eyebrow">Add student</p>

      {/* ── Student details form ── */}
      <form className="mu-form mu-inline-form-grid" onSubmit={handleSave}>
        <div className="mu-inline-row">
          <label className="mu-inline-label">First Name <span className="mu-required">*</span></label>
          <input className="mu-input" type="text" placeholder="First name"
            value={form.firstName} onChange={(e) => set('firstName', e.target.value)}
            required disabled={studentSaved} />
        </div>
        <div className="mu-inline-row">
          <label className="mu-inline-label">Last Name <span className="mu-required">*</span></label>
          <input className="mu-input" type="text" placeholder="Last name"
            value={form.lastName} onChange={(e) => set('lastName', e.target.value)}
            required disabled={studentSaved} />
        </div>
        <div className="mu-inline-row">
          <label className="mu-inline-label">Email <span className="mu-required">*</span></label>
          <input className="mu-input mu-input-readonly" type="email" placeholder="Email address"
            value={form.email} readOnly />
        </div>
        <div className="mu-inline-row">
          <label className="mu-inline-label">Grade</label>
          <select className="mu-select" value={form.grade}
            onChange={(e) => set('grade', e.target.value)} disabled={studentSaved}>
            <option value="">Select grade…</option>
            {[1,2,3,4,5,6,7,8,9,10].map((g) => (
              <option key={g} value={String(g)}>Grade {g}</option>
            ))}
          </select>
        </div>

        {!studentSaved && (
          <div className="mu-form-foot">
            <button className="mu-dark-btn" type="submit"
              disabled={saveState.loading || !form.firstName || !form.lastName || !form.email}>
              {saveState.loading ? <LoadingDots /> : 'Add student'}
            </button>
            {saveState.error && <p className="error-text">{saveState.error}</p>}
          </div>
        )}
      </form>

      {/* ── Enrollment section — revealed after student saved ── */}
      {studentSaved && (
        <div className="mu-enroll-section">
          {/* Added so far */}
          {enrollments.length > 0 && (
            <div className="mu-enroll-added">
              {enrollments.map((e) => (
                <div key={e.courseId} className="mu-enroll-chip">
                  {e.title}{e.subject ? ` · ${e.subject}` : ''}
                  <StatusBadge status={e.status || 'active'} />
                </div>
              ))}
            </div>
          )}

          {/* Add another enrollment */}
          <form className="mu-form mu-inline-form-grid mu-enroll-form" onSubmit={handleEnroll}>
            <div className="mu-inline-row">
              <label className="mu-inline-label">Course</label>
              {coursesLoading ? (
                <p className="mu-muted">Loading courses…</p>
              ) : (
                <select className="mu-select" value={enrForm.courseId}
                  onChange={(e) => setEnr('courseId', e.target.value)} required>
                  <option value="">Select a course…</option>
                  {availableCourses.map((c) => (
                    <option key={c._id} value={String(c._id)}>
                      {c.title} · Grade {c.grade} · {c.subject}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="mu-inline-row">
              <label className="mu-inline-label">Status</label>
              <select className="mu-select" value={enrForm.status}
                onChange={(e) => setEnr('status', e.target.value)}>
                {ENROLLMENT_STATUSES_ADD.map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="mu-form-foot">
              <button className="mu-dark-btn" type="submit"
                disabled={enrState.loading || !enrForm.courseId}>
                {enrState.loading ? <LoadingDots /> : 'Add enrollment'}
              </button>
              {enrState.error && <p className="error-text">{enrState.error}</p>}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ─── StatCard ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, clickable, onClick }) {
  return (
    <div
      className={`mu-card sd-stat-card${clickable ? ' sd-stat-card--clickable' : ''}`}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter') onClick(); } : undefined}
    >
      <p className="eyebrow">{label}</p>
      <p className="sd-stat-value">{value ?? '—'}</p>
      {sub && <p className="sd-stat-sub">{sub}</p>}
      {clickable && <span className="sd-stat-drill">View all →</span>}
    </div>
  );
}

// ─── DemosModal ───────────────────────────────────────────────────────────────

function DemosModal({ demos, onClose }) {
  return (
    <div className="sd-modal-overlay" onClick={onClose}>
      <div className="sd-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sd-modal-head">
          <p className="eyebrow">Demos in Queue</p>
          <button className="mu-ghost-btn" onClick={onClose}>Close</button>
        </div>
        {demos.length === 0 ? (
          <p className="mu-muted">No upcoming demos scheduled.</p>
        ) : (
          <div className="sd-modal-list">
            {demos.map((demo) => (
              <div key={demo.id} className="sd-modal-row">
                <div className="sd-modal-row-main">
                  <p className="sd-modal-name">{demo.prospectName || demo.email}</p>
                  <p className="mu-muted">{demo.email}</p>
                </div>
                <div className="sd-modal-row-meta">
                  <p className="sd-modal-mentor">{demo.mentor?.name || '—'}</p>
                  <p className="mu-muted">{formatDate(demo.timeSlot)}</p>
                </div>
                <StatusBadge status={demo.status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── SalesDashboard ───────────────────────────────────────────────────────────

function SalesDashboard() {
  const [stats, setStats]       = useState(null);
  const [demos, setDemos]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([getDemoQueue(), getEnrollmentStats()])
      .then(([queueData, enrollData]) => {
        setStats({
          demosInQueue: queueData.demosInQueue,
          demosThisWeek: queueData.demosThisWeek,
          activeStudents: enrollData.activeStudents,
          newStudentsThisMonth: enrollData.newStudentsThisMonth,
        });
        setDemos(queueData.demos || []);
      })
      .catch((err) => setError(err.message || 'Unable to load dashboard.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="mu-card"><p className="mu-muted">Loading dashboard…</p></div>;
  if (error)   return <div className="mu-card"><p className="error-text">{error}</p></div>;

  return (
    <div className="sd-container">
      <div className="sd-stats-grid">
        <StatCard
          label="Demos in Queue"
          value={stats.demosInQueue}
          sub="upcoming scheduled demos"
          clickable
          onClick={() => setModalOpen(true)}
        />
        <StatCard
          label="Active Students"
          value={stats.activeStudents}
          sub="with active enrollments"
        />
        <StatCard
          label="Demos This Week"
          value={stats.demosThisWeek}
          sub="scheduled this week"
        />
        <StatCard
          label="New Students"
          value={stats.newStudentsThisMonth}
          sub="added this month"
        />
      </div>

      <div className="mu-card">
        <p className="eyebrow" style={{ marginBottom: '1.2rem' }}>Demo pipeline</p>
        {demos.length === 0 ? (
          <p className="mu-muted">No upcoming demos scheduled.</p>
        ) : (
          <div className="sd-table-wrap">
            <table className="sd-table">
              <thead>
                <tr>
                  <th>Prospect</th>
                  <th>Email</th>
                  <th>Mentor</th>
                  <th>Scheduled</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {demos.map((demo) => (
                  <tr key={demo.id}>
                    <td>{demo.prospectName || '—'}</td>
                    <td className="sd-cell-muted">{demo.email}</td>
                    <td>{demo.mentor?.name || '—'}</td>
                    <td className="sd-cell-muted">{formatDate(demo.timeSlot)}</td>
                    <td><StatusBadge status={demo.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && <DemosModal demos={demos} onClose={() => setModalOpen(false)} />}
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

function ManageUsersPage() {
  const currentUser = useCurrentUser();

  const [email, setEmail]           = useState('');
  const [searching, setSearching]   = useState(false);
  const [searchError, setSearchError] = useState('');
  const [result, setResult]         = useState(null); // null=pristine, false=not-found, object=found
  const [searched, setSearched]     = useState(false);

  const [mentors, setMentors]             = useState([]);
  const [mentorsLoading, setMentorsLoading] = useState(true);
  const [courses, setCourses]             = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [prospectInvites, setProspectInvites] = useState([]);
  const [inviteLoading, setInviteLoading]   = useState(false);
  const normalizedEmail = email.trim().toLowerCase();

  // Load mentors + courses on mount
  useEffect(() => {
    getMentors()
      .then((d) => setMentors(d?.mentors || []))
      .catch(() => {})
      .finally(() => setMentorsLoading(false));

    listCourses()
      .then((d) => setCourses(d?.courses || []))
      .catch(() => {})
      .finally(() => setCoursesLoading(false));
  }, []);

  const fetchProspectInvites = useCallback(() => {
    if (!searched || !normalizedEmail) {
      setProspectInvites([]);
      setInviteLoading(false);
      return;
    }
    const eligible = result === false || result?.role === 'prospect';
    if (!eligible) {
      setProspectInvites([]);
      setInviteLoading(false);
      return;
    }
    setInviteLoading(true);

    const fetchInvites = getDemoInvites(normalizedEmail)
      .then((data) => setProspectInvites(data?.invites || []))
      .catch(() => setProspectInvites([]));

    // If we're in the "no account" state and an invite was just sent, the
    // prospect user now exists — re-lookup so the UI transitions from
    // "No account" to "Prospect found" automatically.
    const refetchUser = result === false
      ? lookupUserByEmail(normalizedEmail)
          .then((data) => { if (data?.exists) setResult(data.user); })
          .catch(() => {})
      : Promise.resolve();

    Promise.all([fetchInvites, refetchUser]).finally(() => setInviteLoading(false));
  }, [result, normalizedEmail, searched]);

  useEffect(() => {
    fetchProspectInvites();
  }, [fetchProspectInvites]);

  const handleSearch = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    const q = normalizedEmail;
    if (!q) return;
    setSearching(true);
    setSearchError('');
    setResult(null);
    setSearched(false);
    try {
      const data = await lookupUserByEmail(q);
      if (data?.exists && !['student', 'prospect'].includes(data.user?.role)) {
        setResult(false);
        setSearchError('This email belongs to a staff account and cannot be managed here.');
      } else {
        setResult(data?.exists ? data.user : false);
      }
    } catch (err) {
      setSearchError(err.message || 'Lookup failed. Please try again.');
    } finally {
      setSearching(false);
      setSearched(true);
    }
  };

  const handleReset = () => {
    setEmail('');
    setResult(null);
    setSearched(false);
    setSearchError('');
    setProspectInvites([]);
    setInviteLoading(false);
  };

  if (currentUser?.role === 'student') {
    return (
      <div className="manage-users-page">
        <div className="mu-card">
          <p className="eyebrow">Access restricted</p>
          <p className="mu-muted">Please sign in with a sales or admin account to access this workspace.</p>
        </div>
      </div>
    );
  }

  const isProspect = result && result?.role === 'prospect';
  const foundUser  = result && typeof result === 'object' && !isProspect;
  const noAccount  = searched && result === false;
  const showProspectPanel = searched && isProspect;
  const isStudent  = foundUser && result?.role === 'student';

  return (
    <div className="manage-users-page">

      {/* ── Search card ── */}
      <div className="mu-card mu-search-card">
        <p className="eyebrow">Sales workspace</p>
        <p className="mu-search-note">
          Look up a student by email to view their profile and manage enrollments,
          or send a demo invitation and register new prospects.
        </p>
        <SearchBar
          value={email}
          onChange={setEmail}
          onSearch={handleSearch}
          loading={searching}
        >
          {(foundUser || noAccount || isProspect) && (
            <div className="mu-search-status-row">
              {foundUser && (
                <div className="mu-found-pill">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Student found
                </div>
              )}
              {noAccount && (
                <div className="mu-notfound-pill">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  No account for <strong>{email}</strong>
                </div>
              )}
              {isProspect && (
                <div className="mu-found-pill">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Prospect found
                </div>
              )}
            </div>
          )}
        </SearchBar>
        {searchError && <p className="error-text mu-mt-sm">{searchError}</p>}
      </div>

      {/* ── Found: student / teacher / admin ── */}
      {foundUser && (
        <>
          {isStudent ? (
            <div className="mu-two-col">
              <div className="mu-card">
                <p className="eyebrow">Student profile</p>
                <StudentProfile user={result} />
              </div>
              <EnrollmentsCard
                student={result}
                courses={courses}
                coursesLoading={coursesLoading}
              />
            </div>
          ) : (
            <div className="mu-card">
              <p className="eyebrow">Student profile</p>
              <StudentProfile user={result} />
            </div>
          )}
        </>
      )}

      {/* ── Not found ── */}
  {(noAccount || showProspectPanel) && (
    <div className="mu-two-col">
      <DemoInviteCard
        searchEmail={email}
        mentors={mentors}
        mentorsLoading={mentorsLoading}
        salesUser={currentUser}
        existingInvites={prospectInvites}
        inviteLoading={inviteLoading}
        onRefreshInvites={fetchProspectInvites}
      />
      <AddUserCard prefillEmail={email} courses={courses} coursesLoading={coursesLoading} />
    </div>
  )}

      {!searched && <SalesDashboard />}

    </div>
  );
}

export default ManageUsersPage;
